import { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { BoardColumn, TaskCard } from '../../services/api/board';
import { usePortalRequests } from '../../hooks/usePortalRequests';
import { RequestRow, type ExtendedTaskCard } from './RequestRow';

/**
 * Phase 57 Plan 57-02 — PORTAL-02
 *
 * Status pill config map — exported so ConfirmationScreen (Plan 57-04) can reuse it
 * without redefining the pill styles.
 */
export const STATUS_PILL: Record<string, { dot: string; bg: string; text: string; label: string }> =
  {
    triage: { dot: '#FC8332', bg: 'bg-orange-50', text: 'text-orange-700', label: 'Triage' },
    in_progress: {
      dot: '#22c55e',
      bg: 'bg-green-50',
      text: 'text-green-700',
      label: 'In progress',
    },
    done: { dot: '#6b7280', bg: 'bg-gray-100', text: 'text-gray-600', label: 'Done' },
    waiting: { dot: '#a855f7', bg: 'bg-purple-50', text: 'text-purple-700', label: 'Waiting on you' },
    submitted: { dot: '#6b7280', bg: 'bg-gray-100', text: 'text-gray-500', label: 'Submitted' },
  };

/** Map a board column to a STATUS_PILL key */
function columnToStatusKey(col: BoardColumn): string {
  if (col.isTriage) return 'triage';
  const lower = col.name.toLowerCase().replace(/\s+/g, '_');
  if (lower === 'done') return 'done';
  if (lower === 'submitted') return 'submitted';
  return 'in_progress';
}

/** "Done / closed" status keys — used to split the Active vs Done filter */
const DONE_STATUS_KEYS = new Set(['done', 'submitted']);

interface FlatTask extends ExtendedTaskCard {
  statusKey: string;
}

function flattenBoard(columns: BoardColumn[]): FlatTask[] {
  const result: FlatTask[] = [];
  for (const col of columns) {
    const statusKey = columnToStatusKey(col);
    for (const task of col.tasks) {
      result.push({ ...task, statusKey } as FlatTask);
    }
  }
  // Sort by createdAt descending (most recent first)
  result.sort((a, b) => {
    const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return db - da;
  });
  return result;
}

interface RequestsRailProps {
  projectId: string;
  selectedTaskId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
}

function SkeletonRow() {
  return (
    <div className="px-4 py-3 border-b border-ms-border animate-pulse">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-4 w-12 bg-ms-border rounded" />
        <div className="h-3 w-16 bg-ms-border rounded ml-auto" />
      </div>
      <div className="h-4 w-3/4 bg-ms-border rounded mb-1" />
      <div className="h-3 w-1/2 bg-ms-border rounded" />
    </div>
  );
}

export function RequestsRail({ projectId, selectedTaskId, onSelect, onNew }: RequestsRailProps) {
  const [activeFilter, setActiveFilter] = useState<'active' | 'done'>('active');
  const { data, isLoading, isError, refetch } = usePortalRequests(projectId);

  const allTasks = data ? flattenBoard(data.columns) : [];
  const activeTasks = allTasks.filter((t) => !DONE_STATUS_KEYS.has(t.statusKey));
  const doneTasks = allTasks.filter((t) => DONE_STATUS_KEYS.has(t.statusKey));

  const openCount = activeTasks.length;
  const waitingCount = activeTasks.filter(
    (t) => t.lastComment !== undefined && t.lastComment.authorExternalUserId === null,
  ).length;

  const displayedTasks: FlatTask[] = activeFilter === 'active' ? activeTasks : doneTasks;

  return (
    <aside className="w-80 flex-shrink-0 border-r border-ms-border overflow-y-auto flex flex-col">
      {/* Header */}
      <div className="px-4 pt-6 pb-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ms-text-muted mb-1">
          CUSTOMER PORTAL
        </p>
        <h1 className="text-3xl font-bold text-ms-text-strong mb-1">Requests</h1>
        <p className="text-xs text-ms-text-secondary">
          {openCount} open
          {waitingCount > 0 ? ` · ${waitingCount} waiting on you` : ''}
        </p>
      </div>

      {/* Active / Done toggle */}
      <div className="px-4 pb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setActiveFilter('active')}
          className={
            activeFilter === 'active'
              ? 'bg-ms-text text-white rounded-full px-3 py-1 text-xs font-medium'
              : 'text-ms-text-secondary text-xs font-medium hover:text-ms-text'
          }
        >
          Active
        </button>
        <button
          type="button"
          onClick={() => setActiveFilter('done')}
          className={
            activeFilter === 'done'
              ? 'bg-ms-text text-white rounded-full px-3 py-1 text-xs font-medium'
              : 'text-ms-text-secondary text-xs font-medium hover:text-ms-text'
          }
        >
          Done
        </button>
      </div>

      {/* New request row */}
      <button
        type="button"
        onClick={onNew}
        className="flex items-center gap-2 px-4 py-3 border-b border-ms-border text-ms-text-secondary hover:bg-ms-surface-2 transition-colors w-full text-left"
      >
        <PlusIcon className="w-4 h-4 text-ms-text-secondary flex-shrink-0" />
        <span className="text-sm font-medium text-ms-text">+ New request</span>
      </button>

      {/* Request list body */}
      <div className="flex-1">
        {isLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : isError ? (
          <div className="px-4 py-6 text-sm text-ms-text-secondary text-center">
            Could not load requests.{' '}
            <button
              type="button"
              onClick={() => refetch()}
              className="text-ms-text underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        ) : displayedTasks.length === 0 ? (
          activeFilter === 'active' ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm font-medium text-ms-text-strong mb-1">No active requests</p>
              <p className="text-xs text-ms-text-secondary mb-4">
                Submit a new request to get started.
              </p>
              <button
                type="button"
                onClick={onNew}
                className="inline-flex items-center gap-1 text-sm font-medium text-ms-text border border-ms-border rounded-lg px-3 py-1.5 hover:bg-ms-surface-2 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                + New request
              </button>
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <p className="text-sm font-medium text-ms-text-strong mb-1">
                No closed requests yet.
              </p>
              <p className="text-xs text-ms-text-secondary">
                Your completed requests will appear here.
              </p>
            </div>
          )
        ) : (
          displayedTasks.map((task) => {
            const pill = STATUS_PILL[task.statusKey];
            const isWaitingOnYou =
              task.lastComment !== undefined && task.lastComment.authorExternalUserId === null;
            const effectivePill =
              isWaitingOnYou && STATUS_PILL['waiting'] ? STATUS_PILL['waiting'] : pill;
            return (
              <RequestRow
                key={task.id}
                task={task as TaskCard & { commentCount?: number }}
                selected={selectedTaskId === task.id}
                onSelect={onSelect}
                pillConfig={effectivePill}
              />
            );
          })
        )}
      </div>
    </aside>
  );
}
